// Pure comms helpers — recipient resolution, opt-out suppression, and message
// body builders. No I/O, unit-testable. The Cloud Function re-resolves
// recipients server-side (authoritative) using the same logic.

// Normalize a contact into an opt-out key: emails lowercased, phones digits-only.
export function optKey(contact, channel) {
  const s = String(contact || '').trim()
  if (channel === 'sms') return s.replace(/[^0-9]/g, '')
  return s.toLowerCase()
}

export function isOptedOut(optOut = {}, contact, channel) {
  const k = optKey(contact, channel)
  return !!k && !!optOut[k]
}

// Resolve recipients from a tournament's registrations for a given channel +
// filter, skipping anyone with no contact on that channel and anyone opted out.
// filter: { scope: 'field'|'division'|'individual', division?, regIds? }
export function resolveRecipients(registrations = {}, filter = {}, { channel = 'email', optOut = {} } = {}) {
  const { scope = 'field', division = null, regIds = null } = filter
  const out = []
  for (const [regId, r] of Object.entries(registrations)) {
    if (!r || typeof r !== 'object') continue
    if (scope === 'division' && division && r.division !== division) continue
    if (scope === 'individual' && Array.isArray(regIds) && !regIds.includes(regId)) continue
    const contact = channel === 'sms' ? r.phone : r.email
    if (!contact) continue
    if (isOptedOut(optOut, contact, channel)) continue
    out.push({
      regId,
      name: r.teamName || (Array.isArray(r.players) ? r.players.join(' / ') : '') || regId,
      email: r.email || null,
      phone: r.phone || null,
      contact,
    })
  }
  return out
}

// Count only — for the compose UI's live "N recipients".
export function recipientCount(registrations, filter, opts) {
  return resolveRecipients(registrations, filter, opts).length
}

// ── Message body builders (blasts) ─────────────────────────────────────────
// Tee sheet: "Name — time (hole)" per group from the day's wave assignments.
export function buildTeeSheetBody(tournament, day = 1) {
  const info = tournament?.info || {}
  const wave = tournament?.teeTimes?.[day]?.waves?.wave1 || {}
  const assignments = wave.assignments || {}
  const lines = []
  for (const [, flight] of Object.entries(tournament?.flights || {})) {
    for (const [tid, team] of Object.entries(flight?.teams || {})) {
      const slot = assignments[tid]
      if (slot == null) continue
      const hole = Number(team?.info?.startingHole)
      lines.push(`${team?.info?.name || tid} — ${slot}${Number.isFinite(hole) ? ` (hole ${hole})` : ''}`)
    }
  }
  lines.sort()
  return `${info.name || 'Tournament'} — Tee Sheet (Day ${day})\n\n${lines.join('\n')}`
}

// Results: flight winners / standings. `rankedByFlight` = [{ flightName, rows:[{name, value}] }].
export function buildResultsBody(tournament, rankedByFlight = []) {
  const info = tournament?.info || {}
  const blocks = rankedByFlight.map(({ flightName, rows }) => {
    const body = (rows || []).slice(0, 10).map((r, i) => `  ${i + 1}. ${r.name}${r.value != null ? ` — ${r.value}` : ''}`).join('\n')
    return `${flightName}\n${body}`
  })
  return `${info.name || 'Tournament'} — Results\n\n${blocks.join('\n\n')}`
}

export const AUTO_MESSAGES = [
  { key: 'confirmation', label: 'Registration confirmation' },
  { key: 'receipt', label: 'Payment receipt' },
  { key: 'teeTime', label: 'Tee-time / pairing notice' },
]
